import React, { useState, useEffect } from 'react';
import tradeoffMatrix from '../data/tradeoffMatrix.json';
import styles from '../styles/DecisionTool.styles';
import { StartButton } from '../styles/CaseStudyHome.styles';
import db from '../firebase/firebaseConfig';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { getGameById } from '../firebase/db';
import { useParams } from 'react-router-dom';
import AdvertisingTable from './AdvertisingTable';
import SubmissionMessage from './SubmissionMessage';
import ResearchDevelopmentTable from './ResearchDevelopmentTable';
import ManufacturingTable from './ManufacturingTable';
import ProfitMarginTable from './ProfitMarginTable';

const DecisionTool = () => {
    const { teamId } = useParams();
    const teamIdParts = teamId.split("_");
    const submissionType = teamIdParts.pop();
    const teamIdParsed = teamIdParts.join("_");


    // Helper function to generate unique session keys for each team
    const getSessionKey = (key) => `${teamId}_${key}`;
    // const getSessionKey = (round, key) => `${teamId}_round${round}_${key}`;

    const [selectedSegments, setSelectedSegments] = useState(() => {
        return JSON.parse(sessionStorage.getItem(getSessionKey('selectedSegments'))) || [];
    });

    const [roundNumber, setRoundNumber] = useState(1);
    const [loading, setLoading] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false); // Track submission status
    const [isRoundLocked, setIsRoundLocked] = useState(false);
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(null);
    const [profitMarginLevel, setProfitMarginLevel] = useState(() => {
        return JSON.parse(sessionStorage.getItem(getSessionKey('profitMarginLevel'))) || 1; // Default value of 1
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [firestoreError, setFirestoreError] = useState('');


    const segmentsOptions = [
        "All Potential",
        "Urban Commuters",
        "Tourism Agencies",
        "Rural",
        "Delivery/Logistics",
        "Health/Fitness",
        "Education",
        "Government"
    ];

    const [selectedLevels, setSelectedLevels] = useState(() => {
        const storedLevels = sessionStorage.getItem(getSessionKey('selectedLevels'));
        return storedLevels ? JSON.parse(storedLevels) : {
            BatteryRange: 1,
            Comfort: 1,
            SmartFeatures: 1,
            Security: 1,
            Safety: 1,
            Speed: 1,
            Storage: 1,
            AllTerrain: 1,
        };
    });

    const [rdUpgrades, setRdUpgrades] = useState(() => {
        const storedUpgrades = sessionStorage.getItem(getSessionKey('rdUpgrades'));
        return storedUpgrades ? JSON.parse(storedUpgrades) : {
            BatteryRange: 0,
            Comfort: 0,
            SmartFeatures: 0,
            Security: 0,
            Safety: 0,
            Speed: 0,
            Storage: 0,
            AllTerrain: 0,
        };
    });

    const [rdDollarUpgrades, setRdDollarUpgrades] = useState(() => {
        const storedUpgrades = sessionStorage.getItem(getSessionKey('rdDollarUpgrades'));
        return storedUpgrades ? JSON.parse(storedUpgrades) : {
            BatteryRange: 0,
            Comfort: 0,
            SmartFeatures: 0,
            Security: 0,
            Safety: 0,
            Speed: 0,
            Storage: 0,
            AllTerrain: 0,
        };
    });

    const [advertisingBudgets, setAdvertisingBudgets] = useState(() => {
        const storedBudgets = sessionStorage.getItem(getSessionKey('advertisingBudgets'));
        return storedBudgets ? JSON.parse(storedBudgets) : {
            Performance: {
                BatteryRange: 0,
                Comfort: 0,
                SmartFeatures: 0,
                Security: 0,
                Safety: 0,
                Speed: 0,
                Storage: 0,
                AllTerrain: 0,
                Broad: 0,
            },
            Brand: {
                BatteryRange: 0,
                Comfort: 0,
                SmartFeatures: 0,
                Security: 0,
                Safety: 0,
                Speed: 0,
                Storage: 0,
                AllTerrain: 0,
                Broad: 0,
            },
        };
    });

    // Update sessionStorage whenever the state changes
    useEffect(() => {
        sessionStorage.setItem(getSessionKey('selectedSegments'), JSON.stringify(selectedSegments));
    }, [getSessionKey, selectedSegments]);

    useEffect(() => {
        sessionStorage.setItem(getSessionKey('profitMarginLevel'), JSON.stringify(profitMarginLevel));
    }, [getSessionKey, profitMarginLevel]);

    useEffect(() => {
        sessionStorage.setItem(getSessionKey('selectedLevels'), JSON.stringify(selectedLevels));
    }, [getSessionKey, selectedLevels]);

    useEffect(() => {
        sessionStorage.setItem(getSessionKey('rdUpgrades'), JSON.stringify(rdUpgrades));
    }, [getSessionKey, rdUpgrades]);

    useEffect(() => {
        sessionStorage.setItem(getSessionKey('rdDollarUpgrades'), JSON.stringify(rdDollarUpgrades));
    }, [getSessionKey, rdDollarUpgrades]);


    useEffect(() => {
        sessionStorage.setItem(getSessionKey('advertisingBudgets'), JSON.stringify(advertisingBudgets));
    }, [advertisingBudgets, getSessionKey]);

    useEffect(() => {
        if (isConfirmationVisible || firestoreError || errorMessage) {
            // Scroll to bottom of the page smoothly
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [errorMessage, firestoreError, isConfirmationVisible]);

    const handleSegmentSelect = (event) => {
        const segment = event.target.value;

        if (segment === "All Potential") {
            // Add all segments (except "All Potential") to the selectedSegments list
            const allSegments = segmentsOptions.filter(s => s !== "All Potential");
            setSelectedSegments(allSegments);
        } else {
            // If the segment is not already selected and it's not "All Potential", add it to the list
            if (!selectedSegments.includes(segment) && segment !== "") {
                setSelectedSegments([...selectedSegments, segment]);
            }
        }
    };

    useEffect(() => {
        const fetchSubmissionStatus = async () => {
            try {
                const docRef = doc(db, submissionType, teamIdParsed);
                const docSnap = await getDoc(docRef);
                // if (docSnap.exists() && docSnap.data().is_submitted) {
                //     setIsSubmitted(docSnap.data().is_submitted);
                // }
                if (docSnap.exists()) {
                    const teamData = docSnap.data();
                    const round1Data = teamData.rounds && teamData.rounds[0];

                    if (round1Data && round1Data.is_submitted) {
                        setIsSubmitted(round1Data.is_submitted);
                    }

                    // Check if round is locked
                    if (teamData.gameId) {
                        const game = await getGameById(teamData.gameId);
                        if (game?.lockedRounds?.includes(1)) {
                            setIsRoundLocked(true);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching submission status:', error);
                setFirestoreError('Error fetching submission status');
            } finally {
                setLoading(false);
            }
        };
        fetchSubmissionStatus();
    }, [teamId]);

    const handleSegmentRemove = (segmentToRemove) => {
        setSelectedSegments(selectedSegments.filter(segment => segment !== segmentToRemove));
    };

    const handleLevelChange = (attribute, level) => {
        const updatedSelectedLevels = { ...selectedLevels };
        updatedSelectedLevels[attribute] = parseInt(level);
        setSelectedLevels(updatedSelectedLevels);
    };

    const handleUpgradeChange = (attribute, upgrade) => {
        const updatedRdUpgrades = { ...rdUpgrades };
        updatedRdUpgrades[attribute] = parseFloat(upgrade / 500000);
        setRdUpgrades(updatedRdUpgrades);

    };

    const handleAdvertisingChange = (type, attribute, value) => {
        const updatedAdvertisingBudgets = { ...advertisingBudgets };
        updatedAdvertisingBudgets[type][attribute] = parseFloat(value);
        setAdvertisingBudgets(updatedAdvertisingBudgets);
    };

    const attributesList = Object.keys(tradeoffMatrix.attributes).filter((attribute) => attribute !== "Price");

    //const totalSum = 8;
    const totalSum = Object.values(selectedLevels).reduce((acc, level) => acc + level, 0);

    const calculateManufacturedBikeValues = () => {
        const manufacturedBikeValues = {};

        attributesList.forEach((rowAttribute) => {
            let tradeoffSum = selectedLevels[rowAttribute];

            attributesList.forEach((columnAttribute) => {
                tradeoffSum += tradeoffMatrix.attributes[columnAttribute].tradeoffs[rowAttribute][selectedLevels[columnAttribute] - 1];
            });

            // manufacturedBikeValues[rowAttribute] = Math.max(tradeoffSum, 1); // Ensure value is non-negative
            manufacturedBikeValues[rowAttribute] = Math.min(Math.max(tradeoffSum, 1), 5);
        });

        return manufacturedBikeValues;
    };

    const manufacturedBikeValues = calculateManufacturedBikeValues();

    const calculateUpgradedBikeValues = () => {
        const upgradedBikeValues = {};

        attributesList.forEach((attribute) => {
            const upgradedValue = manufacturedBikeValues[attribute] + rdUpgrades[attribute];
            upgradedBikeValues[attribute] = Math.min(Math.max(upgradedValue, 1), 5);
        });

        return upgradedBikeValues;
    };

    const upgradedBikeValues = calculateUpgradedBikeValues();

    const handleSubmit = async () => {
        const totalCost = calculateTotalCost(selectedLevels);

        if (validateSubmission()) {
            const dataToSubmit = {
                selectedLevels,
                rdUpgrades,
                advertisingBudgets,
                selectedSegments,
                totalCost,
                upgradedBikeValues,
            };


            try {
                const teamDocRef = doc(db, submissionType, teamIdParsed);

                // Fetch the current team data to preserve round 2 data
                const teamSnap = await getDoc(teamDocRef);
                const teamData = teamSnap.exists() ? teamSnap.data() : null;

                // Prepare updated rounds data, ensuring round 2 remains intact
                const updatedRounds = teamData?.rounds || [];
                updatedRounds[0] = {
                    roundNumber: 1,
                    manufacturing: {
                        attributes: selectedLevels,
                        total_cost: totalCost,
                        upgraded_bike: upgradedBikeValues,
                    },
                    rnd: {
                        upgrades: rdUpgrades,
                    },
                    advertising: {
                        segments: selectedSegments,
                        performance_ads: advertisingBudgets.Performance,
                        brand_ads: advertisingBudgets.Brand,
                    },
                    profit_margin: {
                        profit_margin_selection: profitMarginLevel,
                        profit_margin_percent: calculateProfitMarginPercentage().toFixed(2),
                        profit_margin_per_bike_dollars: calculateProfitMarginPerBike().toFixed(2),
                        price_per_bike_dollars: calculatePricePerBike().toFixed(2),
                        cost_per_bike_dollars: calculateCostPerBike().toFixed(2),
                    },
                    is_submitted: true,
                };

                // Write back the entire updated rounds array
                await updateDoc(teamDocRef, { rounds: updatedRounds });

                console.log("Data successfully submitted to Firestore.");
                setIsConfirmationVisible(false); // Close the confirmation after submission
                setIsSubmitted(true); // Lock the page on submission
                setFirestoreError('');
            } catch (error) {
                console.error("Error updating Firestore:", error);
                setFirestoreError('Failed to submit data. Please register your team again.');
            }
        }
    };


    const calculateTotalCost = (selectedLevels) => {
        // Add your logic to calculate the total cost based on selectedLevels
        let cost = 0;
        for (let key in selectedLevels) {
            cost += selectedLevels[key];
        }
        return cost;
    };

    const [isRndError, setIsRndError] = useState(false); // error handling state for R&D
    const [totalRndCost, setTotalRndCost] = useState(0); // total R&D cost state

    useEffect(() => {
        // Get the total R&D cost by summing the values of the rdUpgrades object
        const totalCost = Object.values(rdUpgrades).reduce((sum, cost) => sum + cost, 0);

        // Determine the budget based on the current round number
        const maxBudget = roundNumber === 3 ? 2000000 : 1500000;

        // Update the total R&D cost and check for error if it exceeds the max budget
        const totalRndCost = totalCost * 500000;
        setTotalRndCost(totalRndCost);

        // Check if the total R&D cost exceeds the max budget
        setIsRndError(totalRndCost > maxBudget);

    }, [rdUpgrades, roundNumber]); // This effect runs whenever rdUpgrades or roundNumber changes

    const calculateTotalAdCost = () => {
        const performanceTotal = Object.values(advertisingBudgets.Performance).reduce((sum, value) => sum + value, 0);
        const brandTotal = Object.values(advertisingBudgets.Brand).reduce((sum, value) => sum + value, 0);
        return performanceTotal + brandTotal;
    };

    const [isAdError, setIsAdError] = useState(false); // State for error checking

    // Check if the advertising budget exceeds the limit
    useEffect(() => {
        // Calculate the total advertising cost
        const totalAdCost = calculateTotalAdCost();

        // Determine the advertising budget limit based on the round number
        const adBudgetLimit = roundNumber === 3 ? 2000000 : 500000;

        // Check if the total advertising cost exceeds the budget limit
        if (totalAdCost > adBudgetLimit) {
            setIsAdError(true);
        } else {
            setIsAdError(false);
        }
    }, [advertisingBudgets, roundNumber]); // Recalculate when advertising budgets or roundNumber change


    const validateSubmission = () => {
        // Calculate total cost (assuming you have the selected levels available)
        const totalCost = calculateTotalCost(selectedLevels);

        // Check if all advertising budgets are still at 0
        const allBudgetsZero = Object.values(advertisingBudgets).every(brand =>
            Object.values(brand).every(value => value === 0)
        );

        // Show error if segments are empty and not all budgets are zero
        if (selectedSegments.length === 0 && !allBudgetsZero) {
            setErrorMessage('Error! Please select at least one segment to target in advertising.');
            return false; // Validation failed
        }

        // Show error if total cost exceeds 25
        if (totalCost > 25) {
            setErrorMessage('Error! You have exceeded your manufacturing budget of 25 credits.');
            return false; // Validation failed
        }

        if (isRndError) {
            setErrorMessage('Error! You have exceeded your R&D budget of $1.5 million.');
            return false;
        }

        if (isAdError) {
            setErrorMessage('Error! You have exceeded your Advertising budget of $500,000.');
            return false;
        }


        // Calculate total R&D budget
        const totalRdBudget = attributesList.reduce((acc, attribute) => {
            return acc + (rdUpgrades[attribute] * 500_000); // Convert R&D input to 500k
        }, 0);

        // Calculate total advertising budget
        const totalAdBudget = attributesList.reduce((acc, attribute) => {
            return acc + advertisingBudgets.Performance[attribute] + advertisingBudgets.Brand[attribute];
        }, 0);

        // Check if total budget exceeds $2 million
        if (totalRdBudget + totalAdBudget + advertisingBudgets.Performance['Broad'] + advertisingBudgets.Brand['Broad'] > 2_000_000) {
            setErrorMessage('Error! You have exceeded your combined R&D and Ads budget of $2 million.');
            return false;
        }

        setErrorMessage(''); // Clear error message if validation passes
        return true; // Validation passed
    };

    const openConfirmation = () => {
        if (validateSubmission()) {
            setIsConfirmationVisible(true);
        }
    };

    const closeConfirmation = () => {
        setIsConfirmationVisible(false);
    };

    // Calculate the profit margin percentage based on the selected level
    const calculateProfitMarginPercentage = () => profitMarginLevel * 10; // 1 = 10%, 2 = 20%, etc.

    // Calculate cost per bike based on the total cost from manufacturing
    const calculateCostPerBike = () => totalSum * 10; // Assuming `totalSum` is your calculated total cost

    // Calculate profit per bike in dollars
    const calculateProfitMarginPerBike = () => calculateCostPerBike() * (calculateProfitMarginPercentage() / 100);

    // Calculate price per bike based on cost and profit margin
    const calculatePricePerBike = () => calculateCostPerBike() + calculateProfitMarginPerBike();


    if (loading) {
        return <div></div>; // Show loading spinner or empty div while loading
    }

    if (isSubmitted) {
        return <SubmissionMessage roundNumber={1} teamId={teamId} />;
    }

    if (isRoundLocked) {
        return (
            <div style={{ maxWidth: '600px', margin: '80px auto', padding: '40px', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
                <h2 style={{ color: '#e65100' }}>Round 1 is Locked</h2>
                <p style={{ color: '#666', fontSize: '16px' }}>Your instructor has locked this round. Submissions are not currently accepted.</p>
            </div>
        );
    }

    return (
        <div className={styles.tableContainer}>
            {/* First table with inputs */}
            <ManufacturingTable
                attributesList={attributesList}
                selectedLevels={selectedLevels}
                tradeoffMatrix={tradeoffMatrix}
                handleLevelChange={handleLevelChange}
                totalSum={totalSum}
                upgradedBikeValues={upgradedBikeValues}
                manufacturedBikeValues={manufacturedBikeValues}
            />

            <div>
                <p></p>
            </div>

            <ResearchDevelopmentTable
                attributesList={attributesList}
                manufacturedBikeValues={manufacturedBikeValues}
                rdUpgrades={rdUpgrades}
                upgradedBikeValues={upgradedBikeValues}
                handleUpgradeChange={handleUpgradeChange}
                budget={2}
                rdDollarUpgrades={rdDollarUpgrades}
                roundNumber={roundNumber}
            />

            <AdvertisingTable
                segmentsOptions={segmentsOptions}
                selectedSegments={selectedSegments}
                handleSegmentSelect={handleSegmentSelect}
                handleSegmentRemove={handleSegmentRemove}
                attributesList={attributesList}
                upgradedBikeValues={upgradedBikeValues}
                advertisingBudgets={advertisingBudgets}
                handleAdvertisingChange={handleAdvertisingChange}
                roundNumber={roundNumber}
            />

            <ProfitMarginTable
                profitMarginLevel={profitMarginLevel}
                setProfitMarginLevel={setProfitMarginLevel}
                calculateProfitMarginPercentage={calculateProfitMarginPercentage}
                calculateCostPerBike={calculateCostPerBike}
                calculateProfitMarginPerBike={calculateProfitMarginPerBike}
                calculatePricePerBike={calculatePricePerBike}
                roundNumber={roundNumber}
            />

            {/* SUBMIT BUTTON */}
            <div>
                {/* <styles.SubmitButton onClick={openConfirmation}>Submit</styles.SubmitButton> */}
                <StartButton onClick={openConfirmation}>Submit</StartButton>
            </div>


            {/* Confirmation Popup */}
            {isConfirmationVisible && (
                <div className={styles.confirmationPopup}>
                    <p> </p>
                    <h3>Are you sure you want to Submit? All Submissions are FINAL.</h3>
                    <button
                        onClick={handleSubmit}
                        style={{ marginBottom: '12px' }}
                    >Yes</button>
                    <> </>
                    <button
                        onClick={closeConfirmation}
                        style={{ marginBottom: '12px' }}
                    >No</button>
                </div>
            )}

            {/* Error message displayed under the submit button */}
            {firestoreError && <styles.errorMessage>{firestoreError}</styles.errorMessage>}
            {errorMessage && <styles.errorMessage>{errorMessage}</styles.errorMessage>}

        </div>
    );
};

export default DecisionTool;